import { ReportData } from '../types/reportTypes';

// Parse currency string to number
const parseCurrency = (value: string): number => {
  if (!value || value === '') return 0;
  return parseFloat(value.replace(/[$,]/g, ''));
};

// Parse percentage string to number
const parsePercentage = (value: string): number => {
  if (!value || value === '') return 0;
  return parseFloat(value.replace(/%/g, '')) / 100;
};

// Calculate the sum of an array of numbers
const sum = (numbers: number[]): number => {
  return numbers.reduce((total, num) => total + num, 0);
};

// Calculate net profit and commission for Cody Cline reports
const calculateCodyNetProfit = (grossProfit: number, companyOH: number): number => {
  // For Cody Cline: Net Profit = Gross Profit - Company OH
  return grossProfit - companyOH;
};

const calculateCodyCommission = (netProfit: number): number => {
  // For Cody Cline: Sales Commission = 50% of Net Profit
  return netProfit / 2;
};

// Calculate the individual job data from the Cody Sales March CSV
// IMPORTANT: Using 13% flat OH calculation (Contract Total × 0.13) retroactive to 1/1/2025
const jobData = [
  {
    jobInfo: "253: Andrew Amari",
    leadSource: "Referral- Cody's network",
    trade: "Windows & Doors",
    contractTotal: 38145.68,
    grossMarginPct: 0.4392,
    grossProfit: 16753.58,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 38145.68 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "193: Hyde Park Buildings 433/435/437",
    leadSource: "Referral- Cody's network",
    trade: "Siding",
    contractTotal: 564352.23,
    grossMarginPct: 0.4708,
    grossProfit: 265697.03,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 564352.23 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "245: Solution Based Property Management",
    leadSource: "Referral- Cody's network",
    trade: "Roofing",
    contractTotal: 1947.32,
    grossMarginPct: 0.5293,
    grossProfit: 1030.72,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 1947.32 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "E-271: Charlie and Nancy Kauffunger",
    leadSource: "Referral- Cody's network",
    trade: "Skylight Replacement",
    contractTotal: 1894.00,
    grossMarginPct: 0.4133,
    grossProfit: 782.79,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 1894.00 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "248: Austen Cohen",
    leadSource: "Referral- Dominic's network",
    trade: "Roofing",
    contractTotal: 11539.00,
    grossMarginPct: 0.3803,
    grossProfit: 4388.28,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 11539.00 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "250: Ellicott Hills Condo 3",
    leadSource: "Referral- Dominic's network",
    trade: "Roofing",
    contractTotal: 1199.00,
    grossMarginPct: 0.6247,
    grossProfit: 749.02,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 1199.00 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "255: Ian & Ella Pisner (170)",
    leadSource: "Previous Customer",
    trade: "Roofing",
    contractTotal: 250.00,
    grossMarginPct: 0.4000,
    grossProfit: 100.00, // 250.00 * 0.40
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 250.00 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "249: Michael & Kathleen Craft",
    leadSource: "Google Organic Search",
    trade: "Roofing",
    contractTotal: 12034.00,
    grossMarginPct: 0.3148,
    grossProfit: 3788.30,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 12034.00 * 0.13, // 13% flat rate
    netProfit: 0
  }
];

// Apply Cody's calculation formula to each job
jobData.forEach(job => {
  job.netProfit = calculateCodyNetProfit(job.grossProfit, job.companyOH);
  job.salesCommission = calculateCodyCommission(job.netProfit);
});

// Calculate sales by source - matching the format needed for charts
const salesBySource = (() => {
  const sourceMap = new Map<string, any>();
  
  jobData.forEach(job => {
    const key = job.leadSource;
    if (!sourceMap.has(key)) {
      sourceMap.set(key, {
        source: key,
        count: 0,
        totalSales: 0,
        grossProfit: 0
      });
    }
    
    const sourceData = sourceMap.get(key);
    sourceData.count += 1;
    sourceData.totalSales += job.contractTotal;
    sourceData.grossProfit += job.grossProfit;
  });
  
  return Array.from(sourceMap.values());
})();

// Create ad spend by source data - all sources have 0 spend for March
const adSpendBySource = (() => {
  const uniqueSources = new Set<string>();
  jobData.forEach(job => uniqueSources.add(job.leadSource));
  
  return Array.from(uniqueSources).map(source => ({
    source: source,
    spend: 0
  }));
})();

// Calculate sales by trade
const salesByTrade = (() => {
  const tradeMap = new Map<string, any>();
  
  jobData.forEach(job => {
    const key = job.trade;
    const normalizedKey = key === "Siding" ? "Siding" : 
                         key === "Roofing" ? "Roofing" : 
                         key === "Windows & Doors" ? "Windows & Doors" : 
                         key === "Skylight Replacement" ? "Other" :
                         "Other";
    
    if (!tradeMap.has(normalizedKey)) {
      tradeMap.set(normalizedKey, {
        trade: normalizedKey,
        count: 0,
        totalSales: 0,
        grossProfit: 0,
        avgContractValue: 0,
        avgGrossMargin: 0
      });
    }
    
    const tradeData = tradeMap.get(normalizedKey);
    tradeData.count += 1;
    tradeData.totalSales += job.contractTotal;
    tradeData.grossProfit += job.grossProfit;
  });
  
  // Calculate averages
  tradeMap.forEach(trade => {
    trade.avgContractValue = trade.count > 0 ? trade.totalSales / trade.count : 0;
    trade.avgGrossMargin = trade.totalSales > 0 ? trade.grossProfit / trade.totalSales : 0;
  });
  
  return Array.from(tradeMap.values());
})();

// Calculate totals
const totalJobs = jobData.length;
const totalSales = sum(jobData.map(job => job.contractTotal));
const totalGrossProfit = sum(jobData.map(job => job.grossProfit));
const totalCompanyOH = sum(jobData.map(job => job.companyOH));
const totalNetProfit = sum(jobData.map(job => job.netProfit));
const totalSalesCommissions = sum(jobData.map(job => job.salesCommission));
const totalManagerFees = 0; // No manager fees for Cody
const totalAdSpend = 0; // No ad spend for March based on CSV

// Export the full report data for March 2025
export const codyMarchData: ReportData = {
  period: {
    month: 3,
    year: 2025
  },
  kpis: {
    adSpend: totalAdSpend,
    totalSales,
    grossProfit: totalGrossProfit,
    salesCommissions: totalSalesCommissions,
    managerFees: totalManagerFees,
    companyOH: totalCompanyOH,
    netProfit: totalNetProfit,
    netProfitPercent: totalSales > 0 ? totalNetProfit / totalSales : 0,
    grossProfitPercent: totalSales > 0 ? totalGrossProfit / totalSales : 0
  },
  salesBySource,
  salesByTrade,
  adSpendBySource,
  jobData
};